import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const Onboarding: React.FC = () => {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleGetStarted = () => {
    navigate('/patient');
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: 800,
    margin: '0 auto',
    padding: 24,
    fontFamily: 'Arial, sans-serif',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: '48px 40px',
    width: '100%',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
  };

  const progressContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 40,
  };

  const dotStyle = (isActive: boolean, isCompleted: boolean): React.CSSProperties => ({
    width: isActive || isCompleted ? 40 : 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: isCompleted ? '#7EB8A8' : isActive ? '#5B9A8B' : '#D4E8E0',
    transition: 'all 0.3s ease',
  });

  const stepLabelStyle: React.CSSProperties = {
    fontSize: 14,
    color: '#7A8B8B',
    marginBottom: 8,
    letterSpacing: 0.5,
  };

  const headingStyle: React.CSSProperties = {
    fontSize: 32,
    fontWeight: 700,
    color: '#2C3E3E',
    marginBottom: 20,
    lineHeight: 1.3,
  };

  const paragraphStyle: React.CSSProperties = {
    fontSize: 18,
    color: '#4A5C5C',
    lineHeight: 1.7,
    marginBottom: 16,
  };

  const roleContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginTop: 24,
    marginBottom: 16,
  };

  const roleCardStyle: React.CSSProperties = {
    backgroundColor: '#F4F9F7',
    borderRadius: 12,
    padding: '20px 24px',
    textAlign: 'left',
    border: '1px solid #D4E8E0',
  };

  const roleTitleStyle: React.CSSProperties = {
    fontSize: 20,
    fontWeight: 600,
    color: '#2C3E3E',
    marginBottom: 6,
  };

  const roleDescStyle: React.CSSProperties = {
    fontSize: 16,
    color: '#4A5C5C',
    lineHeight: 1.6,
  };

  const featureListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    marginTop: 24,
    marginBottom: 16,
    textAlign: 'left',
  };

  const featureItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    fontSize: 18,
    color: '#4A5C5C',
    lineHeight: 1.6,
  };

  const featureIconStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: '#E8F3EF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
    marginTop: 2,
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    marginTop: 40,
  };

  const buttonBaseStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 600,
    padding: '14px 36',
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: '#5B9A8B',
    color: '#FFFFFF',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: '#E8F3EF',
    color: '#2C6B5E',
  };

  const getStartedButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: '#5B9A8B',
    color: '#FFFFFF',
    padding: '16px 48',
    fontSize: 20,
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <div style={stepLabelStyle}>Step 1 of 4</div>
            <h1 style={headingStyle}>Welcome to Care Diary</h1>
            <p style={paragraphStyle}>
              Care Diary helps families caring for someone with dementia stay
              connected, organised, and informed.
            </p>
            <p style={paragraphStyle}>
              Keep track of daily care, share updates with family and carers, and
              build a clear picture of wellbeing over time — all in one simple,
              easy-to-use place.
            </p>
          </>
        );

      case 2:
        return (
          <>
            <div style={stepLabelStyle}>Step 2 of 4</div>
            <h1 style={headingStyle}>Your Role</h1>
            <p style={paragraphStyle}>
              Everyone in Care Diary has a role. Here's what each one means:
            </p>
            <div style={roleContainerStyle}>
              <div style={roleCardStyle}>
                <div style={roleTitleStyle}>Family</div>
                <div style={roleDescStyle}>
                  View care updates, read daily logs, and stay informed about your
                  loved one's wellbeing.
                </div>
              </div>
              <div style={roleCardStyle}>
                <div style={roleTitleStyle}>Carer</div>
                <div style={roleDescStyle}>
                  Record daily care entries, note observations, and keep the care
                  team up to date.
                </div>
              </div>
              <div style={roleCardStyle}>
                <div style={roleTitleStyle}>Managing Carer</div>
                <div style={roleDescStyle}>
                  Manage the care team, invite new members, and oversee all care
                  records and settings.
                </div>
              </div>
            </div>
          </>
        );

      case 3:
        return (
          <>
            <div style={stepLabelStyle}>Step 3 of 4</div>
            <h1 style={headingStyle}>Getting Started</h1>
            <p style={paragraphStyle}>
              Here are the key features you'll use day to day:
            </p>
            <div style={featureListStyle}>
              <div style={featureItemStyle}>
                <div style={featureIconStyle}>D</div>
                <div>
                  <strong>Daily Diary</strong> — Record meals, mood, activities,
                  and any notable moments from the day.
                </div>
              </div>
              <div style={featureItemStyle}>
                <div style={featureIconStyle}>W</div>
                <div>
                  <strong>Wellbeing Tracker</strong> — See trends over time so you
                  can spot changes early.
                </div>
              </div>
              <div style={featureItemStyle}>
                <div style={featureIconStyle}>F</div>
                <div>
                  <strong>Family Updates</strong> — Share updates so everyone
                  stays in the loop, even from afar.
                </div>
              </div>
              <div style={featureItemStyle}>
                <div style={featureIconStyle}>P</div>
                <div>
                  <strong>Patient Profile</strong> — Keep important details,
                  preferences, and medical info in one place.
                </div>
              </div>
            </div>
          </>
        );

      case 4:
        return (
          <>
            <div style={stepLabelStyle}>Step 4 of 4</div>
            <h1 style={headingStyle}>All Set!</h1>
            <p style={paragraphStyle}>
              You're ready to start using Care Diary. Everything you need to
              support your loved one's care is right here.
            </p>
            <p style={paragraphStyle}>
              Tap "Get Started" to view the patient profile and begin recording
              care updates.
            </p>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={progressContainerStyle}>
          <div style={dotStyle(step === 1, step > 1)} />
          <div style={dotStyle(step === 2, step > 2)} />
          <div style={dotStyle(step === 3, step > 3)} />
          <div style={dotStyle(step === 4, step > 4)} />
        </div>

        {renderStepContent()}

        <div style={buttonContainerStyle}>
          {step > 1 && (
            <button style={secondaryButtonStyle} onClick={handleBack}>
              Back
            </button>
          )}
          {step < 4 && (
            <button style={primaryButtonStyle} onClick={handleNext}>
              Next
            </button>
          )}
          {step === 4 && (
            <button style={getStartedButtonStyle} onClick={handleGetStarted}>
              Get Started
            </button>
          )}
        </div>
      </div>
    </div>
  );
};